/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

interface PerformanceChartProps {
  data: any[];
  loading?: boolean;
  currentPoints?: number;
}

export default function PerformanceChart({ data, loading, currentPoints = 0 }: PerformanceChartProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (loading || !isMounted) {
    return (
      <div className="bg-surface-container-low p-6 h-[400px] w-full rounded-xl border border-outline-variant/10 animate-pulse flex flex-col justify-center items-center">
        <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/20">Loading Your Progress...</p>
      </div>
    );
  }

  let chartData = data.map(item => ({
    ...item,
    date: item.recordedAt ? new Date(item.recordedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : `Week ${item.weekNumber}`
  }));

  // Recharts LineChart needs at least 2 points to draw a line
  if (chartData.length === 0) {
    chartData = [
      { date: "Start", pointsEarned: 0 },
      { date: "Current", pointsEarned: currentPoints }
    ];
  } else if (chartData.length === 1) {
    chartData = [
      { ...chartData[0], date: "Start", pointsEarned: 0 },
      ...chartData
    ];
  }

  return (
    <div className="bg-surface-container-low p-6 h-[400px] w-full flex flex-col gap-4 rounded-xl border border-outline-variant/10 overflow-hidden shadow-sm text-left">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-semibold text-on-surface">Your Progress</h3>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#2563eb]" />
            <span className="text-[10px] font-bold text-on-surface-variant/60 uppercase tracking-widest">Points Earned</span>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full -ml-4 min-h-[300px]">
        <ResponsiveContainer width="100%" height={300} minWidth={0}>
          <LineChart 
            data={chartData} 
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid 
              stroke="#e2e8f0" 
              strokeDasharray="0" 
              vertical={true} 
              horizontal={true} 
              opacity={0.5}
            />
            <XAxis 
              dataKey="date" 
              axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
              tickLine={{ stroke: '#e2e8f0' }}
              tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
              dy={10}
            />
            <YAxis 
              axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
              tickLine={{ stroke: '#e2e8f0' }}
              tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
              dx={-10}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#ffffff', 
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
                color: '#1e293b',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
              }}
              cursor={{ stroke: '#2563eb', strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            <Line 
              type="monotone" 
              dataKey="pointsEarned" 
              stroke="#2563eb" 
              strokeWidth={2}
              dot={{ r: 4, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, fill: '#2563eb', strokeWidth: 3, stroke: '#fff' }}
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
